import React, { useEffect, useMemo, useState } from "react";
import { Alert, Input, Radio, Select, Spin, Table, Tag } from "antd";
import { Link } from "react-router-dom";

import { DEMO_BASE, cleanRef } from "./ExampleLinks";
import { getExampleConversations, getExampleUsage } from "./prospectService";
import styles from "./style.module.scss";

// Pivony ops: what visitors asked the example demos (app.pivony.com/demos/<slug>), with the example
// each chat was on (bridal, yoga, ...), the link label and the phone tab. The API keeps them 30 days.
// Staff only (@pivony.com); the API refuses others.

const CHANNEL_LABEL = { website: "Website", instagram: "Instagram", whatsapp: "WhatsApp" };
const when = (value) => (value ? new Date(value).toLocaleString() : "");

/** "Bridal boutique · Atelier Blanc" plus the language, for a conversation row. */
export function demoTitle(row) {
  return [row.sector_label, row.name || row.slug].filter(Boolean).join(" · ");
}

/** The visitor's first own question: the page asks the example's first question on its own. */
export function firstQuestion(turns) {
  const asked = (turns || []).filter((t) => t.role === "user");
  const own = asked.find((t) => !t.auto) || asked[0];
  return own ? own.text : "";
}

const Transcript = ({ turns }) => (
  <ol className={styles.demoTranscript}>
    {(turns || []).map((turn, i) => (
      <li key={i} className={turn.role === "user" ? styles.demoTurnUser : styles.demoTurnBot}>
        <span className={styles.demoTurnMeta}>
          {turn.role === "user" ? "Visitor" : "Assistant"}
          {turn.auto ? " · asked by the page" : ""} · {when(turn.at)}
        </span>
        <p>{turn.text}</p>
      </li>
    ))}
  </ol>
);

const DemoConversations = () => {
  const [days, setDays] = useState(7);
  const [slug, setSlug] = useState("");
  const [label, setLabel] = useState("");
  const [examples, setExamples] = useState([]);
  const [state, setState] = useState({ loading: true });
  const ref = cleanRef(label);

  useEffect(() => {
    getExampleUsage(30)
      .then((data) => setExamples(data?.examples || []))
      .catch(() => setExamples([]));
  }, []);

  useEffect(() => {
    let stop = false;
    setState((prev) => ({ ...prev, loading: true }));
    getExampleConversations({ days, slug, ref, limit: 200 })
      .then((data) => !stop && setState({ loading: false, rows: data?.conversations || [] }))
      .catch((error) => !stop && setState({ loading: false, rows: [], error: error.code || "unavailable" }));
    return () => {
      stop = true;
    };
  }, [days, slug, ref]);

  const options = useMemo(
    () => [
      { value: "", label: "All demos" },
      ...["tr", "en"].map((lang) => ({
        label: lang === "tr" ? "Turkish" : "English",
        options: examples
          .filter((ex) => ex.lang === lang)
          .map((ex) => ({ value: ex.slug, label: `${ex.sector_label} · ${ex.name}` })),
      })),
    ],
    [examples]
  );

  if (state.error === "forbidden") {
    return (
      <div className={styles.listPage}>
        <Alert type="info" showIcon message="Only Pivony team accounts (@pivony.com) can read demo conversations." />
      </div>
    );
  }

  return (
    <div className={styles.listPage}>
      <div className={styles.pageHeader}>
        <div>
          <h1>Demo conversations</h1>
          <p>
            What visitors asked the example demos, newest first, with the demo each chat was on. Kept 30 days.
            Open counts and link labels are on{" "}
            <Link to="/console/sonic-prospect/example-links">Example demo links</Link>.
          </p>
        </div>
        <Radio.Group value={days} onChange={(e) => setDays(e.target.value)} optionType="button">
          <Radio.Button value={1}>Today</Radio.Button>
          <Radio.Button value={7}>7 days</Radio.Button>
          <Radio.Button value={30}>30 days</Radio.Button>
        </Radio.Group>
      </div>

      <div className={styles.exampleLinkRow}>
        <Select value={slug} onChange={setSlug} options={options} style={{ minWidth: 300 }} aria-label="Demo" />
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Link label, e.g. loho-bride"
          aria-label="Link label"
          allowClear
          style={{ maxWidth: 260 }}
        />
      </div>

      {state.error ? (
        <Alert type="warning" showIcon message="Conversations are unavailable right now. Try again shortly." />
      ) : null}
      <Spin spinning={state.loading}>
        <Table
          size="small"
          rowKey="id"
          dataSource={state.rows || []}
          pagination={{ pageSize: 25, hideOnSinglePage: true }}
          locale={{ emptyText: "No demo conversations in this period." }}
          expandable={{ expandedRowRender: (row) => <Transcript turns={row.turns} /> }}
          columns={[
            { title: "Last activity", dataIndex: "last_at", key: "last_at", render: when, width: 170 },
            {
              title: "Demo",
              key: "demo",
              render: (_, row) => (
                <span>
                  <a href={`${DEMO_BASE}/${row.slug}`} target="_blank" rel="noopener noreferrer">
                    {demoTitle(row)}
                  </a>{" "}
                  {row.lang ? <Tag>{row.lang.toUpperCase()}</Tag> : null}
                </span>
              ),
            },
            { title: "Link label", dataIndex: "ref", key: "ref", render: (value) => value || "—" },
            {
              title: "Channel",
              dataIndex: "channel",
              key: "channel",
              render: (value) => CHANNEL_LABEL[value] || value,
            },
            {
              title: "First question",
              key: "question",
              ellipsis: true,
              render: (_, row) => firstQuestion(row.turns),
            },
            { title: "Answers", dataIndex: "answers", key: "answers", align: "right", width: 90 },
            {
              title: "",
              dataIndex: "limited",
              key: "limited",
              width: 110,
              render: (value) => (value ? <Tag color="orange">Hit the limit</Tag> : null),
            },
          ]}
        />
      </Spin>
    </div>
  );
};

export default DemoConversations;
